import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section } from "../components/shared/Section";
import { SEO } from "../components/shared/SEO";

export default function MusicVideoProductionNortheast() {

  const regionalSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Music Video Production in Northeast India",
    "description": "Professional music video production services across Northeast India including Assam, Meghalaya, Arunachal Pradesh and nearby regions.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Hydrox Studios",
      "areaServed": {
        "@type": "AdministrativeArea",
        "name": "Northeast India"
      }
    }
  };

  return (
    <div className="text-white">
      <SEO
        title="Music Video Production in Northeast India | Professional Video Studio"
        description="Hydrox Studios offers professional music video production services across Northeast India including Assam and surrounding states."
        path="/music-video-production-northeast-india"
        schema={regionalSchema}
      />

      {/* Hero */}
      <Section className="pt-32 pb-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 max-w-4xl mx-auto"
        >
          Music Video Production in Northeast India
        </motion.h1>

        <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
          Cinematic music video production services for artists across Assam,
          Meghalaya, Arunachal Pradesh and the wider Northeast region.
        </p>
      </Section>

      {/* Content */}
      <Section>
        <div className="max-w-4xl mx-auto text-muted-foreground space-y-8 leading-relaxed">

          <h2 className="text-2xl font-syne font-semibold text-white">
            Expanding Beyond Guwahati
          </h2>

          <p>
            While Hydrox Studios operates from Guwahati, our music video
            production services extend across Northeast India. Artists and
            labels from neighboring states collaborate with structured
            production planning and coordinated execution.
          </p>

          <p>
            Regional productions require efficient equipment logistics,
            disciplined scheduling and strong post-production systems to ensure
            consistent output quality.
          </p>

          <h2 className="text-2xl font-syne font-semibold text-white">
            States We Serve
          </h2>

          <ul className="list-disc list-inside space-y-3">
            <li>Assam</li>
            <li>Meghalaya</li>
            <li>Arunachal Pradesh</li>
            <li>Nagaland</li>
            <li>Manipur</li>
            <li>Mizoram</li>
            <li>Tripura</li>
            <li>Sikkim</li>
          </ul>

          <h2 className="text-2xl font-syne font-semibold text-white">
            Structured Regional Production Workflow
          </h2>

          <p>
            Professional music video production across Northeast India involves
            pre-production coordination, travel planning and centralized post-
            production handling.
          </p>

          <p>
            Our workflow ensures smooth execution regardless of shoot location,
            with editing and color grading managed through a disciplined system.
          </p>

          <h2 className="text-2xl font-syne font-semibold text-white">
            Looking for Music Video Production Near You?
          </h2>

          <p>
            If you're searching for music video production near you in
            Northeast India, structured planning and experienced direction are
            essential for professional results.
          </p>

          <p>
            Learn more about our{" "}
            <Link
              to="/music-video-production-guwahati"
              className="text-primary underline"
            >
              Music Video Production in Guwahati
            </Link>{" "}
            and how we manage projects regionally.
          </p>

        </div>
      </Section>

      {/* CTA */}
      <Section className="text-center pb-24">
        <h2 className="font-syne text-3xl md:text-4xl font-bold mb-6">
          Start Your Regional Music Video Project
        </h2>

        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
          Contact Hydrox Studios to discuss your music video production across
          Northeast India.
        </p>

        <Link to="/contact">
          <Button size="lg" className="px-8 py-6">
            Get a Quote
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </Link>
      </Section>
    </div>
  );
}